/**
 * Keyword impact (REQ-019 FR-019-13).
 *
 * The growth engine draws the report on the host every morning from Search
 * Console, the index inspections, the pushes to the search engines and the
 * assistant citations; this page only shows it. The report is one
 * self-contained HTML file with no script, so it is framed with every sandbox
 * restriction on: nothing in it can run or reach this page.
 */
import { useQuery } from "@tanstack/react-query";
import { LineChart } from "lucide-react";
import apiClient from "@/services/api/apiClient";

interface KeywordReport {
  html: string;
  generatedAt: string;
}

export default function KeywordReportPage() {
  const report = useQuery({
    queryKey: ["staff", "keyword-report"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: KeywordReport }>(
        "/staff/marketing/aio/keyword-report",
      );
      return response.data.data;
    },
    retry: false,
  });

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <LineChart className="h-5 w-5 text-sky-600" /> Keyword impact
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Which search phrases move, where the opportunity is, what each phrase
          brings, and whether each page is in Google&apos;s index.
          {report.data &&
            ` Drawn ${new Date(report.data.generatedAt).toLocaleString("en-GB", { timeZone: "UTC" })} UTC.`}
        </p>
      </div>

      {report.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {report.isError && (
        <p className="text-sm text-gray-600">
          No keyword report yet. The daily growth run writes it every morning.
        </p>
      )}
      {report.data && (
        <iframe
          title="Keyword impact report"
          srcDoc={report.data.html}
          sandbox=""
          className="h-[80vh] w-full rounded-lg border border-gray-200 bg-white"
        />
      )}
    </div>
  );
}
